// JRD Fuel & Auto - Utility Functions

import { HOLIDAY_FILE_CONFIG } from '../constants';

// ============================================================================
// FORMATTING
// ============================================================================

/**
 * Format currency with $ symbol and 2 decimal places
 */
export function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(amount);
}

/**
 * Format fuel price (3 decimal places, 9/10 suffix)
 */
export function formatFuelPrice(price: number): string {
  const dollars = Math.floor(price);
  const cents = Math.round((price - dollars) * 1000);
  return `$${dollars}.${cents.toString().padStart(3, '0').slice(0, 2)}⁹`;
}

/**
 * Format percentage with sign
 */
export function formatPercent(value: number, decimals = 1): string {
  const sign = value >= 0 ? '+' : '';
  return `${sign}${value.toFixed(decimals)}%`;
}

/**
 * Format margin (cents per gallon)
 */
export function formatMargin(margin: number): string {
  const cents = Math.round(margin * 100);
  return `${cents}¢/gal`;
}

/**
 * Format gallons with commas
 */
export function formatGallons(gallons: number): string {
  return new Intl.NumberFormat('en-US').format(Math.round(gallons)) + ' gal';
}

/**
 * Format distance in miles
 */
export function formatDistance(miles: number): string {
  return `${miles.toFixed(1)} mi`;
}

/**
 * Format time relative to now
 */
export function formatRelativeTime(date: Date): string {
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHour = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHour / 24);

  if (diffSec < 60) return 'just now';
  if (diffMin < 60) return `${diffMin} min ago`;
  if (diffHour < 24) return `${diffHour} hr ago`;
  if (diffDay < 7) return `${diffDay} day${diffDay > 1 ? 's' : ''} ago`;
  return date.toLocaleDateString();
}

// ============================================================================
// CALCULATIONS
// ============================================================================

/**
 * Calculate fuel margin (retail - wholesale)
 */
export function calculateMargin(retailPrice: number, wholesalePrice: number): number {
  return retailPrice - wholesalePrice;
}

/**
 * Calculate parts price with markup
 */
export function calculatePartsPrice(cost: number, markupPercent: number): number {
  return cost * (1 + markupPercent / 100);
}

/**
 * Calculate distance between two coordinates (Haversine formula)
 */
export function calculateDistance(
  lat1: number,
  lng1: number,
  lat2: number,
  lng2: number
): number {
  const R = 3959; // Earth's radius in miles
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) * Math.sin(dLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

function toRad(deg: number): number {
  return deg * (Math.PI / 180);
}

/**
 * Calculate tank percentage
 */
export function calculateTankPercent(currentGallons: number, capacityGallons: number): number {
  return Math.round((currentGallons / capacityGallons) * 100);
}

// ============================================================================
// HOLIDAY FILE PROCESSING
// ============================================================================

/**
 * Check if account is a contra account (ends in R)
 */
export function isContraAccount(account: string): boolean {
  if (!account) return false;
  const trimmed = account.trim();
  return HOLIDAY_FILE_CONFIG.contraAccountPatterns.some((pattern) => pattern.test(trimmed));
}

/**
 * Parse amount string to number
 */
export function parseAmount(amountStr: string): number {
  if (!amountStr) return 0;
  // Remove $ and commas, handle parentheses for negatives
  let cleaned = amountStr.replace(/[$,\s]/g, '');
  if (cleaned.startsWith('(') && cleaned.endsWith(')')) {
    cleaned = '-' + cleaned.slice(1, -1);
  }
  const parsed = parseFloat(cleaned);
  return isNaN(parsed) ? 0 : parsed;
}

/**
 * Check if vendor ID is valid for import
 */
export function isValidVendor(vendorId: string): boolean {
  return (HOLIDAY_FILE_CONFIG.validVendorIds as readonly string[]).includes(vendorId);
}

/**
 * Process Holiday AP CSV row - returns true if row should be kept
 */
export function shouldKeepAPRow(account: string, amount: number, vendorId?: string): boolean {
  // Remove contra accounts
  if (isContraAccount(account)) return false;
  // Remove zeros
  if (HOLIDAY_FILE_CONFIG.removeZeros && amount === 0) return false;
  // Remove negatives for AP
  if (HOLIDAY_FILE_CONFIG.removeNegativesForAP && amount < 0) return false;
  // Check vendor if provided
  if (vendorId && !isValidVendor(vendorId)) return false;
  return true;
}

/**
 * Process Holiday Sales CSV row - returns true if row should be kept
 */
export function shouldKeepSalesRow(account: string, amount: number): boolean {
  // Remove contra accounts
  if (isContraAccount(account)) return false;
  // Remove zeros
  if (HOLIDAY_FILE_CONFIG.removeZeros && amount === 0) return false;
  // Keep negatives for sales (returns)
  return true;
}

/**
 * Process Holiday Deposit CSV row - returns true if row should be kept
 */
export function shouldKeepDepositRow(amount: number): boolean {
  // Only remove zeros
  return amount !== 0;
}

// ============================================================================
// VALIDATION
// ============================================================================

/**
 * Validate email format
 */
export function isValidEmail(email: string): boolean {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
}

/**
 * Validate password strength
 */
export function isValidPassword(password: string): { valid: boolean; errors: string[] } {
  const errors: string[] = [];
  if (password.length < 8) errors.push('At least 8 characters');
  if (!/[A-Z]/.test(password)) errors.push('At least one uppercase letter');
  if (!/[a-z]/.test(password)) errors.push('At least one lowercase letter');
  if (!/[0-9]/.test(password)) errors.push('At least one number');
  return { valid: errors.length === 0, errors };
}

/**
 * Validate GasBuddy station ID
 */
export function isValidGasBuddyId(id: string): boolean {
  return /^\d{4,6}$/.test(id);
}

// ============================================================================
// MISC UTILITIES
// ============================================================================

/**
 * Generate unique ID
 */
export function generateId(): string {
  return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
}

/**
 * Debounce function
 */
export function debounce<T extends (...args: unknown[]) => unknown>(
  fn: T,
  delay: number
): (...args: Parameters<T>) => void {
  let timeoutId: ReturnType<typeof setTimeout>;
  return (...args: Parameters<T>) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => fn(...args), delay);
  };
}

/**
 * Sleep for specified milliseconds
 */
export function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Clamp number between min and max
 */
export function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

/**
 * Deep clone object
 */
export function deepClone<T>(obj: T): T {
  return JSON.parse(JSON.stringify(obj));
}

/**
 * Get competitor color based on pricing strategy
 */
export function getCompetitorColor(
  yourPrice: number,
  theirPrice: number,
  strategy: 'match' | 'undercut' | 'premium'
): 'green' | 'yellow' | 'red' {
  const diff = theirPrice - yourPrice;
  const threshold = 0.02; // 2 cents

  switch (strategy) {
    case 'match':
      return Math.abs(diff) <= threshold ? 'yellow' : diff > 0 ? 'green' : 'red';
    case 'undercut':
      return diff > 0 ? 'green' : diff < -threshold ? 'red' : 'yellow';
    case 'premium':
      return diff < 0 ? 'green' : diff > threshold ? 'red' : 'yellow';
    default:
      return 'yellow';
  }
}

/**
 * Get tank status based on level
 */
export function getTankStatus(
  percentFull: number,
  reorderPercent: number
): 'ok' | 'low' | 'critical' {
  if (percentFull <= reorderPercent * 0.5) return 'critical';
  if (percentFull <= reorderPercent) return 'low';
  return 'ok';
}
